
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { UsersIcon, ClockIcon, TrendingUpIcon, AlertTriangleIcon, CheckCircleIcon, BarChart3Icon, SearchIcon, UserCheckIcon, UserXIcon, EditIcon, KeyIcon, FileTextIcon, DownloadIcon, Building2Icon, TrashIcon, PlusIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChatNotifications from "@/components/ChatNotifications";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatPersonName } from "@/utils/mask";
import { formatPhoneNumber } from "@/utils/mask";

interface Ticket {
  id: string;
  title: string;
  description: string;
  type: string;
  category: string;
  priority: string;
  status: string;
  queue_level?: string;
  transfer_count?: number;
  requester: { id: number; name: string; email: string } | null;
  assignee: { id: number; name: string; email: string } | null;
  company: { id: number; name: string; domain: string } | null;
  sla_remaining: string;
  sla_expired: boolean;
  created_at: string;
}

interface User {
  id: number | string;
  email: string;
  name: string;
  role: string;
  company_id: number | null;
  company: { id: number; name: string; domain: string } | null;
  is_active: boolean;
  preferred_queue?: string;
  is_master?: boolean;
}

interface InternalUser {
  id: number;
  name: string;
  email: string;
  role: string;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [selectedQueue, setSelectedQueue] = useState("all");
  const [selectedQueueLevel, setSelectedQueueLevel] = useState("all"); // N1, N2, N3
  const [selectedTechnician, setSelectedTechnician] = useState("all");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>("");
  const [internalUsers, setInternalUsers] = useState<InternalUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const isMasterAdminUser = currentUser?.role === "admin" && !currentUser?.company_id;
  const [brandingForm, setBrandingForm] = useState({
    logo_url: "",
    primary_color: "#2563eb",
    secondary_color: "#4f46e5"
  });
  const [lastSavedBranding, setLastSavedBranding] = useState({
    logo_url: "",
    primary_color: "#2563eb",
    secondary_color: "#4f46e5"
  });
  const [brandingCompanyName, setBrandingCompanyName] = useState("");
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingUploading, setBrandingUploading] = useState(false);
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{id: number, name: string} | null>(null);
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [targetCategoryForDelete, setTargetCategoryForDelete] = useState<string>("");
  const [categoriesFromDb, setCategoriesFromDb] = useState<any[]>([]);
  const [isExportingNPS, setIsExportingNPS] = useState(false);
  const [npsExportFormat, setNpsExportFormat] = useState<'csv' | 'excel' | 'xlsx'>('xlsx');
  const [avgResolutionTime, setAvgResolutionTime] = useState("0h");
  const [satisfactionScore, setSatisfactionScore] = useState("N/A");

  useEffect(() => {
    const user = getUser();
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const parsedUser = JSON.parse(user);
      setCurrentUser(parsedUser);
      // Verificar se é admin ou internal
      if (parsedUser.role !== "admin" && parsedUser.role !== "internal") {
        navigate("/chamados");
        return;
      }
    } catch (error) {
      navigate("/login");
      return;
    }

    fetchTickets();
    fetchInternalUsers();
    fetchCategories();
    fetchDashboardSummary();

    // Listener para mudanças de usuário
    const handleUserChange = () => {
      const updatedUser = getUser();
      if (!updatedUser) {
        navigate("/login");
        return;
      }

      try {
        const parsedUser = JSON.parse(updatedUser);
        setCurrentUser(parsedUser);
        // Verificar se é admin ou internal
        if (parsedUser.role !== "admin" && parsedUser.role !== "internal") {
          navigate("/chamados");
          return;
        }
        // Recarregar dados quando usuário mudar
        fetchTickets();
        fetchInternalUsers();
        fetchDashboardSummary();
      } catch (error) {
        navigate("/login");
      }
    };

    window.addEventListener("userChanged", handleUserChange);

    return () => {
      window.removeEventListener("userChanged", handleUserChange);
    };
  }, [navigate]);


  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      const response = await fetch(getApiEndpoint("/api/tickets?limit=1000"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchInternalUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = getToken();
      
      const response = await fetch(getApiEndpoint("/api/tickets/internal/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInternalUsers(data.users || []);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários internos:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        getApiEndpoint("/api/tickets/reports/generate?reportType=summary"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao buscar resumo do relatório");
      }

      const reportData = await response.json();
      const summary = reportData.summary || {};
      setAvgResolutionTime(summary.average_resolution_time || "0h");

      const npsAverage = reportData.npsAverage;
      if (npsAverage === null || npsAverage === undefined || Number.isNaN(Number(npsAverage))) {
        setSatisfactionScore("N/A");
      } else {
        const scoreFive = Number(npsAverage) / 2;
        setSatisfactionScore(`${scoreFive.toFixed(1)}/5`);
      }
    } catch (error) {
      console.error("Erro ao buscar resumo do dashboard:", error);
      setAvgResolutionTime("0h");
      setSatisfactionScore("N/A");
    }
  };

  async function handleSaveBranding() {
    if (!currentUser?.company_id) return;
    try {
      setBrandingSaving(true);
      const token = getToken();
      const payload = {
        logo_url: brandingForm.logo_url?.trim() || null,
        primary_color: brandingForm.primary_color || null,
        secondary_color: brandingForm.secondary_color || null
      };

      const response = await fetch(getApiEndpoint(`/api/companies/${currentUser.company_id}`), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || t.adminDashboard.brandingSaveError);
      }

    setLastSavedBranding({
      logo_url: brandingForm.logo_url?.trim() || "",
      primary_color: brandingForm.primary_color || "#2563eb",
      secondary_color: brandingForm.secondary_color || "#4f46e5"
    });

      toast({
        title: t.adminDashboard.brandingUpdated,
        description: t.adminDashboard.brandingUpdatedDesc,
      });

      window.dispatchEvent(new Event("userChanged"));
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error?.message || t.adminDashboard.brandingSaveError,
        variant: "destructive",
      });
    } finally {
      setBrandingSaving(false);
    }
  }

  async function handleBrandingLogoUpload(file: File) {
    if (!file) return;
    try {
      setBrandingUploading(true);
      const token = getToken();
      const formDataUpload = new FormData();
      formDataUpload.append("logo", file);

      const response = await fetch(getApiEndpoint("/api/companies/upload-logo"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || t.adminDashboard.logoUploadError);
      }

      if (data?.logo_url) {
        setBrandingForm((prev) => ({ ...prev, logo_url: data.logo_url }));
        toast({
          title: t.adminDashboard.logoUploaded,
          description: t.adminDashboard.logoUploadedDesc,
        });
      }
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error?.message || t.adminDashboard.logoUploadError,
        variant: "destructive",
      });
    } finally {
      setBrandingUploading(false);
    }
  }


  const handleExportReport = async (reportFormat: 'pdf' | 'csv' | 'excel') => {
    try {
      const token = getToken();
      
      // Calcular período do último mês
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      
      const startDateStr = format(startDate, "yyyy-MM-dd");
      const endDateStr = format(endDate, "yyyy-MM-dd");
      
      toast({
        title: "Gerando relatório...",
        description: "Aguarde enquanto preparamos o relatório.",
      });

      // Gerar relatório
      const response = await fetch(
        getApiEndpoint(`/api/tickets/reports/generate?startDate=${startDateStr}&endDate=${endDateStr}&reportType=summary`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao gerar relatório");
      }

      const reportData = await response.json();
      
      // Preparar dados para exportação
      const data = {
        period: {
          start: format(new Date(startDateStr), "dd/MM/yyyy", { locale: ptBR }),
          end: format(new Date(endDateStr), "dd/MM/yyyy", { locale: ptBR })
        },
        summary: reportData.summary || {
          total: 0,
          open: 0,
          resolved: 0,
          closed: 0,
          average_resolution_time: '0h',
          average_response_time: '0h',
          sla_compliance: 0,
          first_resolution_rate: 0
        },
        byCategory: reportData.byCategory || {},
        byTechnician: reportData.byTechnician || {},
        reopenRate: reportData.reopenRate || 0,
        npsAverage: reportData.npsAverage || 0,
        slaBreaches: reportData.slaBreaches || 0
      };

      // Exportar no formato solicitado
      if (reportFormat === 'csv') {
        const csvRows: string[] = [];
        const delimiter = ';';
        const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const pushRow = (columns: unknown[]) => {
          csvRows.push(columns.map(escapeCsv).join(delimiter));
        };

        pushRow(['Relatório', 'Chamados']);
        pushRow(['Período', `${data.period.start} a ${data.period.end}`]);
        csvRows.push('');
        pushRow(['Resumo', '']);
        pushRow(['Total', data.summary.total]);
        pushRow(['Abertos', data.summary.open]);
        pushRow(['Resolvidos', data.summary.resolved]);
        pushRow(['Fechados', data.summary.closed]);
        pushRow(['Tempo Médio de Resolução', data.summary.average_resolution_time || '0h']);
        pushRow(['Tempo Médio de Resposta', data.summary.average_response_time || '0h']);
        pushRow(['SLA Cumprido', `${data.summary.sla_compliance || 0}%`]);
        csvRows.push('');
        pushRow(['Chamados por Categoria', '']);
        pushRow(['Categoria', 'Quantidade']);
        Object.entries(data.byCategory).forEach(([category, count]) => {
          pushRow([category, count]);
        });
        
        const csvContent = csvRows.join('\n');
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio_performance_${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: "Exportado com sucesso",
          description: "Arquivo CSV baixado",
        });
      } else if (reportFormat === 'excel') {
        const csvRows: string[] = [];
        const delimiter = ';';
        const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const pushRow = (columns: unknown[]) => {
          csvRows.push(columns.map(escapeCsv).join(delimiter));
        };

        pushRow(['Relatório', 'Satisfação do Usuário']);
        pushRow(['Período', `${data.period.start} a ${data.period.end}`]);
        csvRows.push('');
        pushRow(['Métrica', 'Valor']);
        pushRow(['Total de Chamados', data.summary.total]);
        pushRow(['Resolvidos', data.summary.resolved]);
        pushRow(['NPS Médio', data.npsAverage || 0]);
        pushRow(['SLA Cumprido', `${data.summary.sla_compliance || 0}%`]);
        pushRow(['Taxa de Reabertura', `${data.reopenRate || 0}%`]);
        pushRow(['Primeira Resolução', `${data.summary.first_resolution_rate || 0}%`]);
        
        const csvContent = csvRows.join('\n');
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `satisfacao_usuario_${format(new Date(), 'yyyy-MM-dd')}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: "Exportado com sucesso",
          description: "Arquivo Excel baixado",
        });
      } else if (reportFormat === 'pdf') {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast({
            title: t.adminDashboard.errorTitle,
            description: "Permita pop-ups para exportar PDF",
            variant: "destructive",
          });
          return;
        }
        
        const escapeHtml = (text: string) => {
          const div = document.createElement('div');
          div.textContent = text;
          return div.innerHTML;
        };
        
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Relatório Mensal de Chamados</title>
              <meta charset="utf-8">
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                h1 { color: #1f2937; }
                h2 { color: #374151; margin-top: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f3f4f6; }
                @media print { 
                  body { padding: 0; }
                  @page { margin: 1cm; }
                }
              </style>
            </head>
            <body>
              <h1>Relatório Mensal de Chamados</h1>
              <p><strong>Período:</strong> ${escapeHtml(data.period.start)} a ${escapeHtml(data.period.end)}</p>
              
              <h2>Resumo</h2>
              <table>
                <tr><th>Métrica</th><th>Valor</th></tr>
                <tr><td>Total de Chamados</td><td>${data.summary.total}</td></tr>
                <tr><td>Abertos</td><td>${data.summary.open}</td></tr>
                <tr><td>Resolvidos</td><td>${data.summary.resolved}</td></tr>
                <tr><td>Fechados</td><td>${data.summary.closed}</td></tr>
                <tr><td>Tempo Médio de Resolução</td><td>${escapeHtml(data.summary.average_resolution_time || '0h')}</td></tr>
                <tr><td>Tempo Médio de Resposta</td><td>${escapeHtml(data.summary.average_response_time || '0h')}</td></tr>
                <tr><td>SLA Cumprido</td><td>${data.summary.sla_compliance || 0}%</td></tr>
                <tr><td>Primeira Resolução</td><td>${data.summary.first_resolution_rate || 0}%</td></tr>
                <tr><td>Taxa de Reabertura</td><td>${data.reopenRate || 0}%</td></tr>
                <tr><td>NPS Médio</td><td>${data.npsAverage || 0}</td></tr>
              </table>
              
              <h2>Chamados por Categoria</h2>
              <table>
                <tr><th>Categoria</th><th>Quantidade</th></tr>
                ${Object.entries(data.byCategory).map(([category, count]) => 
                  `<tr><td>${escapeHtml(category)}</td><td>${count}</td></tr>`
                ).join('')}
              </table>
              
              ${Object.keys(data.byTechnician).length > 0 ? `
              <h2>Chamados por Técnico</h2>
              <table>
                <tr><th>Técnico</th><th>Quantidade</th></tr>
                ${Object.entries(data.byTechnician).map(([technician, count]) => 
                  `<tr><td>${escapeHtml(technician)}</td><td>${count}</td></tr>`
                ).join('')}
              </table>
              ` : ''}
              
              <p style="margin-top: 30px; font-size: 12px; color: #6b7280;">
                Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}
              </p>
            </body>
          </html>
        `;
        
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        
        toast({
          title: "Abrindo impressão",
          description: "Use a opção 'Salvar como PDF' na janela de impressão",
        });
      }
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível exportar o relatório",
        variant: "destructive",
      });
    }
  };

  const handleExportNPS = async (formatType: 'csv' | 'excel' | 'xlsx') => {
    try {
      setIsExportingNPS(true);
      const token = getToken();
      
      toast({
        title: "Exportando votações NPS...",
        description: "Aguarde enquanto preparamos o arquivo.",
      });

      const response = await fetch(
        getApiEndpoint(`/api/tickets/reports/nps-export?format=${formatType}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Erro desconhecido" }));
        throw new Error(errorData.error || "Erro ao exportar votações NPS");
      }

      // Fazer download do arquivo
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      const fallbackExtension = formatType === 'excel' ? 'xls' : formatType;
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `votacoes_nps_${format(new Date(), "yyyy-MM-dd")}.${fallbackExtension}`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const formatLabel = formatType === 'excel' ? 'Excel (XLS)' : formatType.toUpperCase();
      toast({
        title: "Exportado com sucesso!",
        description: `Arquivo ${formatLabel} baixado com todas as votações NPS.`,
      });
    } catch (error: any) {
      console.error("Erro ao exportar votações NPS:", error);
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível exportar as votações NPS.",
        variant: "destructive",
      });
    } finally {
      setIsExportingNPS(false);
    }
  };

  const handleOpenAssignmentDialog = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setSelectedAssigneeId(ticket.assignee?.id?.toString() || "");
    setAssignmentDialogOpen(true);
  };

  const handleOpenTicketDetails = (ticketId: string) => {
    navigate(`/atendimento/chamado/${ticketId}`);
  };

  const handleAssignTicket = async () => {
    if (!selectedTicket || !selectedAssigneeId) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Por favor, selecione um responsável.",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(
        getApiEndpoint(`/api/tickets/${selectedTicket.id}/transfer`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assignee_id: parseInt(selectedAssigneeId),
          }),
        }
      );

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: "Chamado atribuído com sucesso!",
        });
        setAssignmentDialogOpen(false);
        setSelectedTicket(null);
        setSelectedAssigneeId("");
        fetchTickets(); // Atualizar lista de chamados
      } else {
        const data = await response.json();
        toast({
          title: t.adminDashboard.errorTitle,
          description: data.error || "Não foi possível atribuir o chamado.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível atribuir o chamado.",
        variant: "destructive",
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/categories"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Garantir que estamos usando todas as categorias ativas
        const activeCategories = (data.categories || []).filter((cat: any) => cat.is_active !== false);
        setCategoriesFromDb(activeCategories);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: t.adminDashboard.errorTitle,
          description: errorData.error || "Não foi possível carregar as categorias.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Por favor, informe o nome da categoria.",
        variant: "destructive",
      });
      return;
    }

    // Verificar se a categoria já existe
    const categoryExists = categoriesFromDb.some(
      cat => cat.name.toLowerCase().trim() === newCategoryName.trim().toLowerCase()
    );

    if (categoryExists) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Esta categoria já existe.",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        toast({
          title: t.adminDashboard.errorTitle,
          description: "Você precisa estar autenticado para adicionar categorias.",
          variant: "destructive",
        });
        return;
      }

      const response = await fetch(getApiEndpoint("/api/categories"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: newCategoryName.trim()
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: t.adminDashboard.categoryAdded,
          description: `A categoria "${newCategoryName.trim()}" foi adicionada com sucesso.`,
        });
        setAddCategoryDialogOpen(false);
        setNewCategoryName("");
        // Recarregar categorias imediatamente
        await fetchCategories();
        fetchTickets();
      } else {
        const errorData = await response.json().catch(() => ({ error: "Erro desconhecido" }));
        throw new Error(errorData.error || "Erro ao adicionar categoria");
      }
    } catch (error: any) {
      console.error("Erro ao adicionar categoria:", error);
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível adicionar a categoria.",
        variant: "destructive",
      });
    }
  };

  const handleOpenEditCategory = (category: string) => {
    try {
      const categoryFromDb = categoriesFromDb.find(c => c.name === category);
      if (categoryFromDb && categoryFromDb.id) {
        setSelectedCategory({ id: categoryFromDb.id, name: categoryFromDb.name });
        setEditedCategoryName(categoryFromDb.name);
        setEditCategoryDialogOpen(true);
      } else {
        toast({
          title: t.adminDashboard.errorTitle,
          description: "Categoria não encontrada. Por favor, recarregue a página.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Erro ao abrir dialog de edição:", error);
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível abrir o dialog de edição.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCategory = async () => {
    if (!editedCategoryName.trim() || !selectedCategory) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Por favor, informe o nome da categoria.",
        variant: "destructive",
      });
      return;
    }

    if (editedCategoryName.trim() === selectedCategory.name) {
      setEditCategoryDialogOpen(false);
      setSelectedCategory(null);
      setEditedCategoryName("");
      return;
    }

    // Verificar se já existe outra categoria com o mesmo nome
    const categoryExists = categoriesFromDb.some(
      cat => cat.id !== selectedCategory.id && cat.name.toLowerCase().trim() === editedCategoryName.trim().toLowerCase()
    );

    if (categoryExists) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Já existe uma categoria com este nome.",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        toast({
          title: t.adminDashboard.errorTitle,
          description: "Você precisa estar autenticado para editar categorias.",
          variant: "destructive",
        });
        return;
      }

      const response = await fetch(getApiEndpoint(`/api/categories/${selectedCategory.id}`), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: editedCategoryName.trim()
        }),
      });

      if (response.ok) {
        toast({
          title: t.adminDashboard.categoryUpdated,
          description: `A categoria foi renomeada de "${selectedCategory.name}" para "${editedCategoryName.trim()}".`,
        });
        setEditCategoryDialogOpen(false);
        setSelectedCategory(null);
        setEditedCategoryName("");
        await fetchCategories();
        fetchTickets();
      } else {
        const errorData = await response.json().catch(() => ({ error: "Erro desconhecido" }));
        throw new Error(errorData.error || "Erro ao atualizar categoria");
      }
    } catch (error: any) {
      console.error("Erro ao atualizar categoria:", error);
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível atualizar a categoria.",
        variant: "destructive",
      });
    }
  };

  const handleOpenDeleteCategory = (category: string) => {
    try {
      const categoryFromDb = categoriesFromDb.find(c => c.name === category);
      if (categoryFromDb && categoryFromDb.id) {
        setSelectedCategory({ id: categoryFromDb.id, name: categoryFromDb.name });
        const otherCategories = categoriesFromDb.filter(c => c.name !== category && c.is_active !== false);
        if (otherCategories.length > 0) {
          setTargetCategoryForDelete(otherCategories[0].id.toString());
        } else {
          toast({
            title: t.adminDashboard.errorTitle,
            description: "Não é possível remover a única categoria existente.",
            variant: "destructive",
          });
          return;
        }
        setDeleteCategoryDialogOpen(true);
      } else {
        toast({
          title: t.adminDashboard.errorTitle,
          description: "Categoria não encontrada. Por favor, recarregue a página.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Erro ao abrir dialog de remoção:", error);
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível abrir o dialog de remoção.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Nenhuma categoria selecionada.",
        variant: "destructive",
      });
      return;
    }

    if (!targetCategoryForDelete) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Por favor, selecione uma categoria de destino para os chamados afetados.",
        variant: "destructive",
      });
      return;
    }

    if (categoriesFromDb.length <= 1) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não é possível remover a única categoria existente.",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        toast({
          title: t.adminDashboard.errorTitle,
          description: "Você precisa estar autenticado para remover categorias.",
          variant: "destructive",
        });
        return;
      }

      const response = await fetch(getApiEndpoint(`/api/categories/${selectedCategory.id}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          targetCategoryId: parseInt(targetCategoryForDelete)
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: t.adminDashboard.categoryRemoved,
          description: `A categoria "${selectedCategory.name}" foi removida. ${data.tickets_moved || 0} chamado(s) foram movidos.`,
        });
        setDeleteCategoryDialogOpen(false);
        setSelectedCategory(null);
        setTargetCategoryForDelete("");
        await fetchCategories();
        fetchTickets();
      } else {
        const errorData = await response.json().catch(() => ({ error: "Erro desconhecido" }));
        throw new Error(errorData.error || "Erro ao remover categoria");
      }
    } catch (error: any) {
      console.error("Erro ao remover categoria:", error);
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível remover a categoria.",
        variant: "destructive",
      });
    }
  };

  const getStatusLabel = (status: string) => {
    const key: Record<string, string> = {
      open: "statusOpen",
      in_progress: "statusInProgress",
      waiting_response: "statusWaiting",
      resolved: "statusResolved",
      closed: "statusClosed",
    };
    return (t.dashboard as Record<string, string>)[key[status]] ?? status;
  };

  const getPriorityLabel = (priority: string) => {
    const key: Record<string, string> = {
      critical: "priorityCritical",
      high: "priorityHigh",
      medium: "priorityMedium",
      low: "priorityLow",
    };
    return (t.dashboard as Record<string, string>)[key[priority]] ?? priority;
  };

  const stats = {
    totalTickets: tickets.length,
    openTickets: tickets.filter(t => t.status === "open").length,
    inProgressTickets: tickets.filter(t => t.status === "in_progress").length,
    slaBreached: tickets.filter(t => t.sla_expired).length,
    avgResolutionTime,
    satisfaction: satisfactionScore
  };

  const technicianStats = tickets.reduce((acc, ticket) => {
    const tech = ticket.assignee?.name || t.adminDashboard.notAssigned;
    if (!acc[tech]) {
      acc[tech] = { total: 0, open: 0, resolved: 0 };
    }
    acc[tech].total++;
    if (ticket.status === "open" || ticket.status === "in_progress") {
      acc[tech].open++;
    } else if (ticket.status === "resolved" || ticket.status === "closed") {
      acc[tech].resolved++;
    }
    return acc;
  }, {} as Record<string, { total: number; open: number; resolved: number }>);

  const categoryStats = tickets.reduce((acc, ticket) => {
    const cat = ticket.category;
    if (!acc[cat]) {
      acc[cat] = 0;
    }
    acc[cat]++;
    return acc;
  }, {} as Record<string, number>);

  const filteredTickets = tickets.filter(ticket => {
    const matchesQueue = selectedQueue === "all" || ticket.category === selectedQueue;
    const matchesQueueLevel = selectedQueueLevel === "all" || ticket.queue_level === selectedQueueLevel;
    const matchesTechnician = selectedTechnician === "all" || ticket.assignee?.name === selectedTechnician;
    return matchesQueue && matchesQueueLevel && matchesTechnician;
  }).sort((a, b) => {
    // Priorizar chamados com SLA expirado no topo
    if (a.sla_expired && !b.sla_expired) return -1;
    if (!a.sla_expired && b.sla_expired) return 1;
    // Depois ordenar por prioridade e data
    const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
    const priorityDiff = (priorityOrder[b.priority as keyof typeof priorityOrder] || 0) - 
                         (priorityOrder[a.priority as keyof typeof priorityOrder] || 0);
    if (priorityDiff !== 0) return priorityDiff;
    // Por último, ordenar por data de criação (mais recentes primeiro)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200";
      case "in_progress": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200";
      case "waiting_response": return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200";
      case "resolved": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200";
      case "closed": return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200";
      case "high": return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200";
      case "medium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200";
      case "low": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.adminDashboard.loadingData}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      {currentUser && <ChatNotifications currentUserId={currentUser.id} />}
      
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.adminDashboard.title}</h1>
          <p className="text-gray-600">{t.adminDashboard.subtitleAlt}</p>
        </div>

        {/* KPIs Principais */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">{t.adminDashboard.totalTickets}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.totalTickets}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">{t.dashboard.open}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.openTickets}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">{t.dashboard.inProgress}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.inProgressTickets}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">{t.dashboard.slaExpired}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.slaBreached}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">{t.adminDashboard.averageTime}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{stats.avgResolutionTime}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">{t.adminDashboard.satisfaction}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.satisfaction}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="queue" className="space-y-6">
          <TabsList className={`grid w-full ${currentUser?.role === "admin" || currentUser?.role === "internal" ? "grid-cols-5" : "grid-cols-4"}`}>
            <TabsTrigger value="queue">{t.adminDashboard.queueTab}</TabsTrigger>
            <TabsTrigger value="technicians">{t.adminDashboard.technicians}</TabsTrigger>
            <TabsTrigger value="users">{t.adminDashboard.users}</TabsTrigger>
            {(currentUser?.role === "admin" || currentUser?.role === "internal") && (
              <TabsTrigger value="companies">{t.adminDashboard.companies}</TabsTrigger>
            )}
            <TabsTrigger value="settings">{t.adminDashboard.settings}</TabsTrigger>
          </TabsList>

          {/* Fila de Atendimento */}
          <TabsContent value="queue" className="space-y-6">
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center dark:text-white">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  {t.adminDashboard.queueTitle}
                </CardTitle>
                <CardDescription className="dark:text-gray-400">
                  {t.adminDashboard.queueDesc}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Select value={selectedQueue} onValueChange={setSelectedQueue}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <SelectValue placeholder={t.adminDashboard.filterByQueue} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.adminDashboard.allQueues}</SelectItem>
                      <SelectItem value="infraestrutura">{t.adminDashboard.queueInfra}</SelectItem>
                      <SelectItem value="hardware">{t.adminDashboard.queueHardware}</SelectItem>
                      <SelectItem value="software">{t.adminDashboard.queueSoftware}</SelectItem>
                      <SelectItem value="seguranca">{t.adminDashboard.queueSecurity}</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={selectedTechnician} onValueChange={setSelectedTechnician}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <SelectValue placeholder={t.adminDashboard.filterByTechnician} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.adminDashboard.allTechnicians}</SelectItem>
                      {Object.keys(technicianStats).map(tech => (
                        <SelectItem key={tech} value={tech}>{tech}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={selectedQueueLevel} onValueChange={setSelectedQueueLevel}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <SelectValue placeholder={t.adminDashboard.filterByLevel} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.adminDashboard.allLevels}</SelectItem>
                      <SelectItem value="N1">🟢 {t.adminDashboard.levelN1}</SelectItem>
                      <SelectItem value="N2">🟡 {t.adminDashboard.levelN2}</SelectItem>
                      <SelectItem value="N3">🔴 {t.adminDashboard.levelN3}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4">
                  {filteredTickets.map((ticket) => (
                    <div 
                      key={ticket.id} 
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenTicketDetails(ticket.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpenTicketDetails(ticket.id);
                        }
                      }}
                      className={`rounded-lg border-2 cursor-pointer transition-colors ${
                        ticket.sla_expired 
                          ? 'border-red-600 bg-red-50 hover:bg-red-100 dark:border-red-900/70 dark:bg-red-950/20 dark:hover:bg-red-950/30' 
                          : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
                      }`}
                      title={t.adminDashboard.clickToOpen}
                    >
                      <div className="p-4">
                        <div className={`flex flex-col lg:flex-row justify-between gap-4 ${
                          ticket.sla_expired ? "lg:items-start" : "lg:items-center"
                        }`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <span className={`font-semibold ${ticket.sla_expired ? 'text-red-900 dark:text-red-200' : 'text-gray-900 dark:text-white'}`}>
                                #{ticket.id}
                              </span>
                              <Badge className={getStatusColor(ticket.status)}>{getStatusLabel(ticket.status)}</Badge>
                              <Badge className={getPriorityColor(ticket.priority)}>{getPriorityLabel(ticket.priority)}</Badge>
                              <Badge className={
                                ticket.queue_level === 'N3' ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-200' :
                                ticket.queue_level === 'N2' ? 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-200' :
                                'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-200'
                              }>
                                {ticket.queue_level || 'N1'}
                              </Badge>
                              {ticket.sla_expired && (
                                <Badge className="bg-red-600 text-white border-red-700 dark:border-red-800">
                                  <AlertTriangleIcon className="h-4 w-4 mr-1" />
                                  {t.adminDashboard.slaExpiredBadge}
                                </Badge>
                              )}
                              {ticket.transfer_count > 0 && (
                                <Badge className="bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-200">
                                  🔄 {t.adminDashboard.transferCount(ticket.transfer_count)}
                                </Badge>
                              )}
                            </div>
                            <h3 className={`font-medium mb-1 ${ticket.sla_expired ? 'text-red-900 dark:text-red-100 font-bold' : 'text-gray-900 dark:text-white'}`}>
                              {ticket.title}
                            </h3>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className={ticket.sla_expired ? "text-red-800 dark:text-red-300 font-semibold" : "text-gray-500 dark:text-gray-400"}>
                                {t.adminDashboard.categoryLabel}: {ticket.category}
                              </span>
                              <span className={ticket.sla_expired ? "text-red-800 dark:text-red-300" : "text-gray-500 dark:text-gray-400"}>•</span>
                              <span className={ticket.sla_expired ? "text-red-800 dark:text-red-300 font-semibold" : "text-gray-500 dark:text-gray-400"}>
                                {t.adminDashboard.requesterLabel}: {ticket.requester?.name || "N/A"}
                              </span>
                              <span className={ticket.sla_expired ? "text-red-800 dark:text-red-300" : "text-gray-500 dark:text-gray-400"}>•</span>
                              <span className={`${ticket.sla_expired ? "text-red-800 bg-red-100 dark:text-red-200 dark:bg-red-900/40 px-2 py-1 rounded border border-red-300 dark:border-red-800 font-semibold" : "text-gray-500 dark:text-gray-400"}`}>
                                {t.adminDashboard.slaLabel}: {ticket.sla_remaining}
                              </span>
                            </div>
                          </div>
                          {!ticket.sla_expired && (
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                              <div className="text-right">
                                <div className="text-sm text-gray-600 dark:text-gray-300">{t.adminDashboard.assigneeLabel}</div>
                                <div className="font-medium text-gray-900 dark:text-white">{ticket.assignee?.name || t.adminDashboard.notAssigned}</div>
                              </div>
                              <Button 
                                variant="default" 
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAssignmentDialog(ticket);
                                }}
                              >
                                {ticket.assignee ? t.adminDashboard.reassign : t.adminDashboard.assign}
                              </Button>
                            </div>
                          )}
                          {ticket.sla_expired && (
                            <div className="text-right">
                              <div className="text-sm font-semibold text-red-800 dark:text-red-300">
                                {t.adminDashboard.assigneeLabel}
                              </div>
                              <div className="font-bold text-base text-red-900 dark:text-red-100">
                                {ticket.assignee?.name || t.adminDashboard.notAssigned}
                              </div>
                              <div className="mt-2 text-xs text-red-700 dark:text-red-400 font-semibold">
                                {t.adminDashboard.urgentAction}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredTickets.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {tickets.length === 0 
                        ? t.adminDashboard.noTicketsQueue
                        : t.adminDashboard.noTicketsFiltered}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Técnicos */}
          <TabsContent value="technicians" className="space-y-6">
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center dark:text-white">
                  <UsersIcon className="h-5 w-5 mr-2" />
                  {t.adminDashboard.techniciansPerformance}
                </CardTitle>
                <CardDescription className="dark:text-gray-400">
                  {t.adminDashboard.techniciansPerformanceDesc}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {Object.entries(technicianStats).map(([tech, stats]) => (
                    <div key={tech} className="border rounded-lg p-4 dark:border-gray-600 dark:bg-gray-700/50">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium text-gray-900 dark:text-white">{tech}</h3>
                        <div className="flex gap-2">
                          <Badge variant="secondary">{stats.total} {t.adminDashboard.total}</Badge>
                          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">{stats.open} {t.adminDashboard.activeMale}</Badge>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">{stats.resolved} {t.adminDashboard.resolved}</Badge>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${Math.min((stats.resolved / stats.total) * 100, 100)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t.adminDashboard.resolutionRate}: {stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0}%
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usuários */}
          <TabsContent value="users" className="space-y-6">
            <UsersManagement />
          </TabsContent>

          {/* Empresas */}
          {(currentUser?.role === "admin" || currentUser?.role === "internal") && (
            <TabsContent value="companies" className="space-y-6">
              <CompaniesManagement />
            </TabsContent>
          )}

          {/* Configurações */}
          <TabsContent value="settings" className="space-y-6">
            {(currentUser?.company_id && (currentUser?.role === "admin" || currentUser?.role === "internal")) && (
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="dark:text-white">{t.adminDashboard.brandingTitle}</CardTitle>
                  <CardDescription className="dark:text-gray-400">
                    {t.adminDashboard.brandingDesc(brandingCompanyName || t.adminDashboard.brandingYourCompany)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {brandingLoading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t.adminDashboard.loadingBranding}</p>
                  ) : (
                    <>
                      <div className="rounded-lg border bg-slate-50/50 p-4 dark:bg-gray-700/40 dark:border-gray-600">
                        <div className="text-sm font-medium text-slate-700 dark:text-gray-200 mb-3">
                          {t.adminDashboard.lastSavedConfig}
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                          {lastSavedBranding.logo_url ? (
                            <div className="flex items-center gap-3">
                              <img
                                src={lastSavedBranding.logo_url.startsWith("http") ? lastSavedBranding.logo_url : getApiEndpoint(lastSavedBranding.logo_url)}
                                alt={t.adminDashboard.logoSaved}
                                className="h-10 w-10 rounded-full object-cover border bg-white dark:bg-gray-600 dark:border-gray-500"
                              />
                              <div className="text-xs text-gray-500 dark:text-gray-400 max-w-[220px] truncate">
                                {lastSavedBranding.logo_url}
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t.adminDashboard.noLogoSaved}</div>
                          )}
                          <div className="flex-1 min-w-[220px]">
                            <div
                              className="h-10 rounded-md border dark:border-gray-600"
                              style={{
                                background: `linear-gradient(135deg, ${lastSavedBranding.primary_color || "#2563eb"} 0%, ${lastSavedBranding.secondary_color || "#4f46e5"} 100%)`
                              }}
                            />
                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3">
                              <span>{t.adminDashboard.primaryColor}: {lastSavedBranding.primary_color || "#2563eb"}</span>
                              <span>{t.adminDashboard.secondaryColor}: {lastSavedBranding.secondary_color || "#4f46e5"}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="dark:text-gray-200">{t.adminDashboard.companyLogo}</Label>
                        {brandingForm.logo_url && (
                          <div className="mt-3 flex items-center gap-3">
                            <img
                              src={brandingForm.logo_url.startsWith("http") ? brandingForm.logo_url : getApiEndpoint(brandingForm.logo_url)}
                              alt={t.adminDashboard.companyLogo}
                              className="h-12 w-12 rounded-full object-cover border dark:border-gray-600 dark:bg-gray-700"
                            />
                            <div className="text-xs text-gray-500 dark:text-gray-400 max-w-[220px] truncate">
                              {brandingForm.logo_url}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                              onClick={() => setBrandingForm((prev) => ({ ...prev, logo_url: "" }))}
                            >
                              {t.adminDashboard.remove}
                            </Button>
                          </div>
                        )}
                        <Input
                          type="file"
                          accept="image/*"
                          className="mt-3 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 file:dark:bg-gray-600 file:dark:text-gray-200"
                          disabled={brandingUploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleBrandingLogoUpload(file);
                            }
                            e.currentTarget.value = "";
                          }}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t.adminDashboard.uploadLogoHint}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="branding-primary-color" className="dark:text-gray-200">{t.adminDashboard.primaryColorLabel}</Label>
                          <div className="flex items-center gap-3 mt-2">
                            <Input
                              id="branding-primary-color"
                              type="color"
                              value={brandingForm.primary_color || "#2563eb"}
                              onChange={(e) => setBrandingForm((prev) => ({ ...prev, primary_color: e.target.value }))}
                              className="w-16 h-10 p-1 dark:border-gray-600 dark:bg-gray-700"
                            />
                            <Input
                              value={brandingForm.primary_color || "#2563eb"}
                              onChange={(e) => setBrandingForm((prev) => ({ ...prev, primary_color: e.target.value }))}
                              placeholder="#2563eb"
                              className="dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="branding-secondary-color" className="dark:text-gray-200">{t.adminDashboard.secondaryColorLabel}</Label>
                          <div className="flex items-center gap-3 mt-2">
                            <Input
                              id="branding-secondary-color"
                              type="color"
                              value={brandingForm.secondary_color || "#4f46e5"}
                              onChange={(e) => setBrandingForm((prev) => ({ ...prev, secondary_color: e.target.value }))}
                              className="w-16 h-10 p-1 dark:border-gray-600 dark:bg-gray-700"
                            />
                            <Input
                              value={brandingForm.secondary_color || "#4f46e5"}
                              onChange={(e) => setBrandingForm((prev) => ({ ...prev, secondary_color: e.target.value }))}
                              placeholder="#4f46e5"
                              className="dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                            />
                          </div>
                        </div>
                      </div>

                      {(brandingForm.primary_color || brandingForm.secondary_color) && (
                        <div className="rounded-lg border p-4 dark:border-gray-600 dark:bg-gray-700/30">
                          <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">{t.adminDashboard.preview}</div>
                          <div
                            className="h-12 rounded-md"
                            style={{
                              background: `linear-gradient(135deg, ${brandingForm.primary_color || "#2563eb"} 0%, ${brandingForm.secondary_color || "#4f46e5"} 100%)`
                            }}
                          />
                        </div>
                      )}

                      <Button onClick={handleSaveBranding} disabled={brandingSaving || brandingUploading} className="dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">
                        {brandingSaving ? t.adminDashboard.saving : t.adminDashboard.saveBranding}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Configuração do Jira */}
            {(currentUser?.company_id && (currentUser?.role === "admin" || currentUser?.role === "internal")) && (
              <JiraIntegrationConfig />
            )}

            <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="dark:text-white">{t.adminDashboard.manageCategories}</CardTitle>
                  <CardDescription className="dark:text-gray-400">
                    {t.adminDashboard.manageCategoriesDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {categoriesFromDb.length > 0 ? (
                    categoriesFromDb.map(category => {
                      const ticketCount = tickets.filter(t => t.category === category.name).length;
                      const isSuperAdmin = currentUser?.role === "admin" && !currentUser?.company_id;
                      const isCompanyCategory = category.company_id && currentUser?.company_id === category.company_id;
                      const canEditCategory = isSuperAdmin || isCompanyCategory;
                      const canRemoveCategory = isSuperAdmin || isCompanyCategory;
                      return (
                        <div key={category.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50 transition-colors">
                          <div className="flex-1">
                            <span className="text-sm font-medium dark:text-gray-100">{t.adminDashboard.categoryDisplayName(category.name)}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              {t.adminDashboard.categoryTicketsCount(ticketCount)}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            {canEditCategory && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white"
                                onClick={() => handleOpenEditCategory(category.name)}
                              >
                                {t.adminDashboard.edit}
                              </Button>
                            )}
                            {canRemoveCategory && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                                onClick={() => handleOpenDeleteCategory(category.name)}
                              >
                                {t.adminDashboard.remove}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      {t.adminDashboard.noCategoriesFound}
                    </p>
                  )}
                  <Button 
                    className="w-full dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                    onClick={() => setAddCategoryDialogOpen(true)}
                  >
                    {t.adminDashboard.addNewCategory}
                  </Button>
                </CardContent>
              </Card>

            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center dark:text-white">
                  <BarChart3Icon className="h-5 w-5 mr-2" />
                  {t.adminDashboard.exportNps}
                </CardTitle>
                <CardDescription className="dark:text-gray-400">
                  {t.adminDashboard.exportNpsDesc}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700">{t.adminDashboard.training}</Badge>
                    <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-700">{t.adminDashboard.qualityControl}</Badge>
                    <Badge variant="outline" className="border-blue-200 text-blue-700 dark:border-blue-600 dark:text-blue-300">CSV</Badge>
                    <Badge variant="outline" className="border-blue-200 text-blue-700 dark:border-blue-600 dark:text-blue-300">XLS</Badge>
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700">{t.adminDashboard.xlsxRecommended}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t.adminDashboard.exportIncludes}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 items-end">
                    <div className="space-y-2">
                      <Label className="dark:text-gray-200">{t.adminDashboard.format}</Label>
                      <Select
                        value={npsExportFormat}
                        onValueChange={(value) => setNpsExportFormat(value as 'csv' | 'excel' | 'xlsx')}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t.adminDashboard.selectFormat} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="xlsx">{t.adminDashboard.xlsxRecommendedOption}</SelectItem>
                          <SelectItem value="excel">{t.adminDashboard.exportXls}</SelectItem>
                          <SelectItem value="csv">{t.adminDashboard.exportCsv}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      className="w-full"
                      onClick={() => handleExportNPS(npsExportFormat)}
                      disabled={isExportingNPS}
                    >
                      <DownloadIcon className="h-4 w-4 mr-2" />
                      {isExportingNPS ? t.adminDashboard.exporting : (npsExportFormat === 'excel' ? t.adminDashboard.exportXls : npsExportFormat === 'xlsx' ? t.adminDashboard.exportXlsx : t.adminDashboard.exportCsv)}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t.adminDashboard.xlsxKeepsFields}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />

      {/* Dialog de Adicionar Categoria */}
      <Dialog open={addCategoryDialogOpen} onOpenChange={setAddCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.addCategoryDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.adminDashboard.addCategoryDialogDesc}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="category-name">{t.adminDashboard.categoryNameLabel}</Label>
              <Input
                id="category-name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={t.adminDashboard.categoryNamePlaceholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCategoryName.trim()) {
                    handleAddCategory();
                  }
                }}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t.adminDashboard.categoryNameHint}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              onClick={() => {
                setAddCategoryDialogOpen(false);
                setNewCategoryName("");
              }}
            >
              {t.adminDashboard.cancel}
            </Button>
            <Button 
              onClick={handleAddCategory} 
              disabled={!newCategoryName.trim()}
            >
              {t.adminDashboard.add}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Editar Categoria */}
      <Dialog open={editCategoryDialogOpen} onOpenChange={setEditCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.editCategoryDialogTitle}</DialogTitle>
            <DialogDescription>
              {selectedCategory?.name != null ? t.adminDashboard.editCategoryDialogDesc(selectedCategory.name) : ""}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-category-name">{t.adminDashboard.newCategoryNameLabel}</Label>
              <Input
                id="edit-category-name"
                value={editedCategoryName}
                onChange={(e) => setEditedCategoryName(e.target.value)}
                placeholder={t.adminDashboard.newCategoryNamePlaceholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editedCategoryName.trim()) {
                    handleUpdateCategory();
                  }
                }}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {tickets.filter(t => t.category === selectedCategory?.name).length} chamado(s) serão atualizados
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              onClick={() => {
                setEditCategoryDialogOpen(false);
                setSelectedCategory(null);
                setEditedCategoryName("");
              }}
            >
              {t.adminDashboard.cancel}
            </Button>
            <Button 
              onClick={handleUpdateCategory} 
              disabled={!editedCategoryName.trim() || editedCategoryName.trim() === selectedCategory?.name}
            >
              {t.adminDashboard.saveChanges}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Remover Categoria */}
      <Dialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.removeCategoryDialogTitle}</DialogTitle>
            <DialogDescription>
              {selectedCategory?.name != null ? t.adminDashboard.removeCategoryDialogDesc(selectedCategory.name) : ""}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/20 dark:border-yellow-700">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Atenção:</strong> Esta ação irá mover {tickets.filter(t => t.category === selectedCategory?.name).length} chamado(s) 
                para outra categoria. A categoria será removida do sistema.
              </p>
            </div>
            <div>
              <Label htmlFor="target-category" className="dark:text-gray-200">Chamados afetados serão movidos para:</Label>
              <Select 
                value={targetCategoryForDelete}
                onValueChange={setTargetCategoryForDelete}
              >
                <SelectTrigger id="target-category">
                  <SelectValue placeholder={t.adminDashboard.selectTargetCategoryPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {categoriesFromDb
                    .filter(cat => cat.name !== selectedCategory?.name && cat.is_active)
                    .map(cat => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Selecione para qual categoria os chamados serão movidos
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              onClick={() => {
                setDeleteCategoryDialogOpen(false);
                setSelectedCategory(null);
                setTargetCategoryForDelete("");
              }}
            >
              {t.adminDashboard.cancel}
            </Button>
            <Button 
              onClick={handleDeleteCategory}
              variant="destructive"
              disabled={!targetCategoryForDelete}
            >
              {t.adminDashboard.removeCategory}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Atribuição */}
      <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedTicket?.assignee ? t.adminDashboard.reassignTicketTitle : t.adminDashboard.assignTicketTitle}
            </DialogTitle>
            <DialogDescription>
              {selectedTicket ? t.adminDashboard.assignTicketDialogDesc(selectedTicket.id) : ""}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t.adminDashboard.ticketTitle}
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">{selectedTicket?.title}</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                {t.adminDashboard.currentAssignee}
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedTicket?.assignee?.name || t.adminDashboard.notAssigned}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                {t.adminDashboard.assignTo}
              </label>
              {loadingUsers ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.adminDashboard.loadingUsers}</p>
              ) : (
                <Select value={selectedAssigneeId} onValueChange={setSelectedAssigneeId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.adminDashboard.selectAssignee} />
                  </SelectTrigger>
                  <SelectContent>
                    {currentUser && (
                      <SelectItem value={currentUser.id.toString()}>
                        {currentUser.name} {t.adminDashboard.me}
                      </SelectItem>
                    )}
                    {internalUsers
                      .filter((user) => !currentUser || user.id !== currentUser.id)
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.name} ({user.role === "admin" ? t.adminDashboard.roleAdminInternal : user.role === "analyst" ? t.adminDashboard.roleAnalyst : t.adminDashboard.roleAdministrator})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAssignmentDialogOpen(false);
                setSelectedTicket(null);
                setSelectedAssigneeId("");
              }}
            >
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleAssignTicket} disabled={!selectedAssigneeId}>
              {t.adminDashboard.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente de configuração do Jira
const JiraIntegrationConfig = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    api_url: "",
    email: "",
    api_token: "",
    project_key: ""
  });
  const [simpleConfig, setSimpleConfig] = useState({
    jira_url: "",
    email: "",
    api_token: ""
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) return;

      const response = await fetch(getApiEndpoint("/api/jira/config?config_type=company"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.config && data.config.company_id && !data.config.user_id && data.config.enabled) {
          setConfig({
            enabled: true,
            api_url: data.config.api_url || "",
            email: data.config.email || "",
            api_token: data.config.api_token || "",
            project_key: data.config.project_key || ""
          });
        } else {
          setConfig({
            enabled: false,
            api_url: "",
            email: "",
            api_token: "",
            project_key: ""
          });
        }
      }
    } catch (error) {
      console.error("Erro ao buscar configuração do Jira:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSimpleConnect = async () => {
    if (!simpleConfig.jira_url || !simpleConfig.email || !simpleConfig.api_token) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    try {
      setConnecting(true);
      const token = getToken();
      if (!token) return;

      const response = await fetch(getApiEndpoint("/api/jira/connect"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...simpleConfig,
          config_type: "company"
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast({
          title: "Jira conectado!",
          description: `Conectado com sucesso ao projeto ${data.config.project_name} (${data.config.project_key})`,
        });
        setSimpleConfig({ jira_url: "", email: "", api_token: "" });
        await fetchConfig();
      } else {
        toast({
          title: "Erro ao conectar",
          description: data.error || "Não foi possível conectar ao Jira",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao conectar com Jira",
        variant: "destructive",
      });
    } finally {
      setConnecting(false);
    }
  };


  if (loading) {
    return (
      <Card className="dark:bg-gray-800 dark:border-gray-700">
        <CardContent className="pt-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">Carregando configuração do Jira...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="dark:bg-gray-800 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="dark:text-white">Integração com Jira da Empresa</CardTitle>
        <CardDescription className="dark:text-gray-400">
          Configure o Jira padrão da empresa. Quando qualquer usuário da empresa criar um chamado, um card será criado automaticamente neste Jira. Esta configuração tem prioridade sobre configurações pessoais dos usuários.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {config.enabled && config.api_url ? (
          <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-900 dark:text-green-100">Jira conectado</p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  {config.api_url} • Projeto: {config.project_key || "Não configurado"}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const token = getToken();
                  if (!token) return;
                  await fetch(getApiEndpoint("/api/jira/config"), {
                    method: "POST",
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      enabled: false,
                      api_url: "",
                      email: "",
                      api_token: "",
                      project_key: "",
                      config_type: "company"
                    }),
                  });
                  await fetchConfig();
                  toast({ title: "Jira desconectado" });
                }}
                className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
              >
                Desconectar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-2">
                Como obter o Token da API:
              </p>
              <ol className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
                <li>Acesse: <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="underline">https://id.atlassian.com/manage-profile/security/api-tokens</a></li>
                <li>Clique em "Create API token"</li>
                <li>Dê um nome para o token e copie o valor gerado</li>
                <li>Cole o token no campo abaixo</li>
              </ol>
            </div>

            <div>
              <Label htmlFor="simple-jira-url" className="dark:text-gray-200">
                URL do seu Jira *
              </Label>
              <Input
                id="simple-jira-url"
                type="url"
                value={simpleConfig.jira_url}
                onChange={(e) => setSimpleConfig({ ...simpleConfig, jira_url: e.target.value })}
                placeholder="https://seu-dominio.atlassian.net"
                className="mt-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <Label htmlFor="simple-jira-email" className="dark:text-gray-200">
                Email da sua conta Jira *
              </Label>
              <Input
                id="simple-jira-email"
                type="email"
                value={simpleConfig.email}
                onChange={(e) => setSimpleConfig({ ...simpleConfig, email: e.target.value })}
                placeholder="seu-email@exemplo.com"
                className="mt-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <Label htmlFor="simple-jira-token" className="dark:text-gray-200">
                Token da API *
              </Label>
              <Input
                id="simple-jira-token"
                type="password"
                value={simpleConfig.api_token}
                onChange={(e) => setSimpleConfig({ ...simpleConfig, api_token: e.target.value })}
                placeholder="Cole aqui o token que você copiou"
                className="mt-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <Button
              onClick={handleSimpleConnect}
              disabled={connecting || !simpleConfig.jira_url || !simpleConfig.email || !simpleConfig.api_token}
              className="w-full dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            >
              {connecting ? "Conectando..." : "Conectar com Jira"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

interface Company {
  id: number;
  name: string;
  domain: string;
  is_active?: boolean;
}

const UsersManagement = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [confirmationWord, setConfirmationWord] = useState("");
  const [userInputConfirmation, setUserInputConfirmation] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const isMasterAdminUser = currentUser?.role === "admin" && !currentUser?.company_id;
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    role: "",
    company_id: "" as string | number | null,
    preferred_queue: "N1"
  });
  const [newPassword, setNewPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "internal" | "analyst" | "external">("internal");
  const [newUserCompanyId, setNewUserCompanyId] = useState<string>("none");
  const [newUserQueue, setNewUserQueue] = useState("N1");
  const [importUsersDialogOpen, setImportUsersDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const isMasterUser = (user: User | null) => {
    if (!user) return false;
    if (user.is_master) return true;
    return typeof user.id === "string" && user.id.startsWith("master:");
  };

  const resolveCompanyIdFromEmail = (email: string): string | null => {
    if (!email) return null;
    const at = email.indexOf("@");
    if (at < 0) return null;
    const emailDomain = email.slice(at + 1).toLowerCase().trim();
    if (!emailDomain) return null;

    // Preferir match mais específico (domínio mais longo) para suportar subdomínios
    const match = (companies || [])
      .filter((c) => c?.domain)
      .map((c) => ({ id: String(c.id), domain: c.domain.toLowerCase().trim() }))
      .filter((c) => emailDomain === c.domain || emailDomain.endsWith(`.${c.domain}`))
      .sort((a, b) => b.domain.length - a.domain.length)[0];

    return match ? match.id : null;
  };

  const resetAddUserForm = () => {
    setNewUserName("");
    setNewUserEmail("");
    setNewUserRole("internal");
    setNewUserCompanyId(isMasterAdminUser ? "none" : "none");
    setNewUserQueue("N1");
  };

  useEffect(() => {
    // Carregar usuário atual do localStorage
    const user = getUser();
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setCurrentUser(parsedUser);
      } catch (error) {
        console.error("Erro ao parsear usuário:", error);
      }
    }
  }, []);

  useEffect(() => {
    // Carregar dados após o usuário estar definido
    if (currentUser) {
      fetchUsers();
      fetchCompanies();
    }
  }, [currentUser]);

  // Branding é carregado no bloco de Configurações (AdminDashboard),
  // não na tela de gestão de usuários.

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      const response = await fetch(getApiEndpoint("/api/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível carregar os usuários.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Importação de empresas fica no bloco "Empresas" (CompaniesManagement)

  const fetchCompanies = async () => {
    try {
      // Admin e internal carregam empresas para seleção
      if (currentUser?.role !== "admin" && currentUser?.role !== "internal") {
        setCompanies([]);
        return;
      }

      const token = getToken();
      // Preferir empresas do DB do tenant (IDs reais do tenant), para evitar mismatch de IDs.
      const tenantResponse = await fetch(getApiEndpoint("/api/companies/tenant"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (tenantResponse.ok) {
        const data = await tenantResponse.json();
        const tenantCompanies = Array.isArray(data.companies) ? data.companies : [];
        if (tenantCompanies.length > 0) {
          setCompanies(tenantCompanies);
          return;
        }
      }

      // Fallback: listar pelo master filtrado por tenant (útil se tenant antigo não tem tabela companies populada ainda)
      const response = await fetch(getApiEndpoint("/api/companies"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(Array.isArray(data.companies) ? data.companies : []);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
      setCompanies([]);
    }
  };

  // Auto-selecionar empresa ao editar usuário (admin): baseado no domínio do e-mail
  useEffect(() => {
    if (!editDialogOpen) return;
    if (currentUser?.role !== "admin") return;
    if (editFormData.role === "admin") return;
    if (editFormData.company_id && editFormData.company_id !== "none") return;
    const companyId = resolveCompanyIdFromEmail(editFormData.email);
    if (companyId) {
      setEditFormData((prev) => ({ ...prev, company_id: companyId }));
    }
  }, [editDialogOpen, editFormData.email, editFormData.role, editFormData.company_id, companies, currentUser?.role]);

  // Auto-selecionar empresa ao criar usuário (admin): baseado no domínio do e-mail
  useEffect(() => {
    if (currentUser?.role !== "admin") return;
    if (newUserCompanyId && newUserCompanyId !== "none") return;
    const companyId = resolveCompanyIdFromEmail(newUserEmail);
    if (companyId) {
      setNewUserCompanyId(companyId);
    }
  }, [newUserEmail, newUserCompanyId, companies, currentUser?.role]);

  const handleToggleStatus = async (userId: number, currentStatus: boolean) => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/users/${userId}/status`), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ is_active: !currentStatus }),
      });

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: `Usuário ${!currentStatus ? "ativado" : "desativado"} com sucesso.`,
        });
        fetchUsers();
      } else {
        const data = await response.json();
        toast({
          title: t.adminDashboard.errorTitle,
          description: data.error || "Não foi possível atualizar o usuário.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível atualizar o usuário.",
        variant: "destructive",
      });
    }
  };

  const handleOpenEditDialog = (user: User) => {
    if (isMasterUser(user)) {
      toast({
        title: "Somente leitura",
        description: "Usuários do banco master são apenas para visualização.",
      });
      return;
    }
    setSelectedUser(user);
    const inferredCompanyId =
      user.company_id
        ? user.company_id.toString()
        : (currentUser?.role === "admin" ? (resolveCompanyIdFromEmail(user.email) || "none") : "none");

    setEditFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      company_id: inferredCompanyId,
      preferred_queue: user.preferred_queue || "N1"
    });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    if (!editFormData.name.trim()) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Nome é obrigatório.",
        variant: "destructive",
      });
      return;
    }

    if (!editFormData.email.trim()) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "E-mail é obrigatório.",
        variant: "destructive",
      });
      return;
    }

    // Empresa obrigatória para perfis não-admin (especialmente quando admin está editando)
    if (
      editFormData.role !== "admin" &&
      (editFormData.company_id === "" || editFormData.company_id === null || editFormData.company_id === "none")
    ) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Empresa é obrigatória para este perfil. Selecione a empresa correspondente ao domínio do e-mail.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();
      
      // Preparar dados para envio
      const updateData: any = {
        name: formatPersonName(editFormData.name),
        email: editFormData.email.trim(),
        preferred_queue: (editFormData.role === "internal" || editFormData.role === "analyst" || editFormData.role === "admin") 
          ? editFormData.preferred_queue 
          : null
      };

      // Admin e internal podem alterar role e company_id (validação no backend)
      if (currentUser?.role === "admin") {
        const companyIdValue = editFormData.role === "admin"
          ? null 
          : (editFormData.company_id === "" || editFormData.company_id === "none" ? null : (typeof editFormData.company_id === "string" ? parseInt(editFormData.company_id) : editFormData.company_id));
        
        updateData.role = editFormData.role;
        updateData.company_id = companyIdValue;
      } else if (currentUser?.role === "internal") {
        const companyIdValue = editFormData.role === "admin"
          ? null
          : (editFormData.company_id === "" || editFormData.company_id === "none" ? null : (typeof editFormData.company_id === "string" ? parseInt(editFormData.company_id) : editFormData.company_id));

        // Internal pode alterar role entre Usuário e Analista (mas não para admin) e não pode alterar company_id
        // Sempre enviar o role se for diferente do atual OU se for internal editando
        if (editFormData.role !== selectedUser.role || currentUser?.role === "internal") {
          updateData.role = editFormData.role;
        }
        updateData.company_id = companyIdValue;
      } else {
        // Outros perfis não alteram role/company_id
      }

      const response = await fetch(getApiEndpoint(`/api/users/${selectedUser.id}`), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({} as any));
        toast({
          title: "Sucesso",
          description: "Usuário atualizado com sucesso.",
        });
        setEditDialogOpen(false);
        // Se mudou a empresa, o filtro pode esconder o usuário. Resetar para mostrar todos.
        const updatedCompanyId = data?.user?.company_id;
        if (
          updateData.company_id !== undefined &&
          companyFilter !== "all" &&
          updatedCompanyId !== undefined &&
          updatedCompanyId !== null &&
          String(updatedCompanyId) !== String(companyFilter)
        ) {
          setCompanyFilter("all");
        }
        fetchUsers();
      } else {
        const data = await response.json();
        toast({
          title: t.adminDashboard.errorTitle,
          description: data.error || "Não foi possível atualizar o usuário.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível atualizar o usuário.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenResetPasswordDialog = (user: User) => {
    if (isMasterUser(user)) {
      toast({
        title: "Somente leitura",
        description: "Usuários do banco master não podem ser alterados aqui.",
      });
      return;
    }
    setSelectedUser(user);
    setNewPassword("");
    setResetPasswordDialogOpen(true);
  };

  const generateRandomWord = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const length = 4 + Math.floor(Math.random() * 3); // 4-6 caracteres
    let word = '';
    for (let i = 0; i < length; i++) {
      word += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return word;
  };

  const handleOpenDeleteUserDialog = (user: User) => {
    if (isMasterUser(user)) {
      toast({
        title: "Somente leitura",
        description: "Usuários do banco master não podem ser excluídos aqui.",
      });
      return;
    }
    setSelectedUser(user);
    const word = generateRandomWord();
    setConfirmationWord(word);
    setUserInputConfirmation("");
    setDeleteUserDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    if (!userInputConfirmation || userInputConfirmation.trim().toUpperCase() !== confirmationWord.toUpperCase()) {
      toast({
        title: "Palavra incorreta",
        description: `A palavra digitada não confere. Digite: ${confirmationWord}`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(`/api/users/${selectedUser.id}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ confirmation_word: userInputConfirmation.trim().toUpperCase() }),
      });

      if (response.ok) {
        toast({
          title: "Usuário excluído",
          description: "O usuário foi excluído permanentemente do sistema.",
        });
        setDeleteUserDialogOpen(false);
        setSelectedUser(null);
        setConfirmationWord("");
        setUserInputConfirmation("");
        await fetchUsers();
      } else {
        const data = await response.json();
        toast({
          title: t.adminDashboard.errorTitle,
          description: data.error || "Não foi possível excluir o usuário.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível excluir o usuário.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    if (!newPassword || newPassword.length < 6) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "A senha deve ter pelo menos 6 caracteres.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(`/api/users/${selectedUser.id}/reset-password`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ new_password: newPassword }),
      });

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: "Senha resetada com sucesso.",
        });
        setResetPasswordDialogOpen(false);
        setNewPassword("");
      } else {
        const data = await response.json();
        toast({
          title: t.adminDashboard.errorTitle,
          description: data.error || "Não foi possível resetar a senha.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível resetar a senha.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };


  const handleCreateUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Nome e e-mail são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    const requiresCompany = newUserRole === "external" || newUserRole === "analyst" || (newUserRole === "admin" && !isMasterAdminUser) || currentUser?.role === "internal";

    // Validar se usuário Usuário/Analista/Admin precisa de empresa vinculada
    if (requiresCompany) {
      let hasCompany = false;
      if (currentUser?.role === "admin" || currentUser?.role === "internal") {
        hasCompany = newUserCompanyId && newUserCompanyId !== "none";
      } else {
        hasCompany = !!currentUser?.company_id;
      }
      
      if (!hasCompany) {
        toast({
          title: t.adminDashboard.errorTitle,
          description: "Selecione a empresa do usuário.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      const token = getToken();
      const payload: Record<string, any> = {
        name: formatPersonName(newUserName),
        email: newUserEmail.trim().toLowerCase(),
        role: newUserRole,
        preferred_queue: newUserQueue,
      };

      if (currentUser?.role === "admin" || currentUser?.role === "internal") {
        if (newUserCompanyId && newUserCompanyId !== "none") {
          payload.company_id = newUserCompanyId;
        }
      } else if (currentUser?.company_id) {
        payload.company_id = currentUser.company_id;
      }

      const response = await fetch(getApiEndpoint("/api/users"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Usuário criado",
          description: "Enviamos um link de redefinição de senha por e-mail.",
        });
        setAddUserDialogOpen(false);
        resetAddUserForm();
        await fetchUsers();
      } else {
        let errorMessage = "Erro ao adicionar usuário";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
          if (errorData.details && process.env.NODE_ENV === 'development') {
            console.error('Detalhes do erro:', errorData.details);
          }
        } catch (parseError) {
          errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Erro ao criar usuário:', error);
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível adicionar o usuário. Verifique os dados e tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      toast({
        title: "Arquivo obrigatório",
        description: "Selecione uma planilha Excel ou CSV para importar.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsImporting(true);
      const token = getToken();
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await fetch(getApiEndpoint("/api/users/import"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        toast({
          title: "Erro ao importar",
          description: data.error || "Não foi possível importar usuários.",
          variant: "destructive",
        });
        return;
      }

      const createdCount = data.created?.length || 0;
      const errorCount = data.errors?.length || 0;

      toast({
        title: "Importação concluída",
        description: `${createdCount} criados, ${errorCount} com erro.`,
      });

      setImportUsersDialogOpen(false);
      setImportFile(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: "Erro ao importar",
        description: "Não foi possível importar usuários.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadUsersTemplate = () => {
    const headers = [
      "nome",
      "email",
      "telefone",
      "dominio_empresa",
      "setor",
      "funcao",
      "perfil",
      "fila_preferencial",
      "ativo"
    ];
    const exampleRows = [
      [
        "João Silva",
        "joao@empresa.com.br",
        "11999999999",
        "empresa.com.br",
        "Financeiro",
        "Analista",
        "analista",
        "",
        "true"
      ],
      [
        "Maria Souza",
        "maria@empresa.com.br",
        "11988887777",
        "empresa.com.br",
        "TI",
        "Suporte",
        "administrador",
        "N1",
        "true"
      ],
      [
        "Carlos Admin",
        "carlos@outra.com.br",
        "11977776666",
        "outra.com.br",
        "Operacoes",
        "Coordenador",
        "administrador",
        "N2",
        "true"
      ]
    ];

    const csvRows = [
      headers.join(";"),
      ...exampleRows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(";"))
    ];
    const csvContent = csvRows.join("\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-importacao-usuarios.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "admin": return t.adminDashboard.roleAdminInternal;
      case "internal": return t.adminDashboard.roleAdministrator;
      case "analyst": return t.adminDashboard.roleAnalyst;
      case "external": return t.adminDashboard.roleUser;
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin": return "bg-purple-100 text-purple-800";
      case "internal": return "bg-blue-100 text-blue-800";
      case "analyst": return "bg-amber-100 text-amber-800";
      case "external": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && user.is_active) ||
      (statusFilter === "inactive" && !user.is_active);
    const userCompanyId = user.company?.id ?? user.company_id;
    const userCompanyIdStr = userCompanyId != null && userCompanyId !== "" ? String(userCompanyId) : null;
    const matchesCompany =
      companyFilter === "all" ||
      (companyFilter === "no-company" && !userCompanyIdStr) ||
      (companyFilter !== "all" && companyFilter !== "no-company" && userCompanyIdStr !== null && userCompanyIdStr === String(companyFilter));

    return matchesSearch && matchesRole && matchesStatus && matchesCompany;
  });

  const uniqueCompanies = Array.from(
    new Set(users.filter(u => u.company).map(u => u.company!.id))
  ).map(id => users.find(u => u.company?.id === id)!.company!);
  const companiesForFilter = (companies && companies.length > 0 ? companies : uniqueCompanies)
    .filter((c: any) => {
      if (!c) return false;
      if (c.is_active === undefined || c.is_active === null) return true;
      return c.is_active === true || c.is_active === 1 || c.is_active === "1";
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <Card className="dark:bg-gray-800 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="flex items-center dark:text-white">
          <UsersIcon className="h-5 w-5 mr-2" />
          {t.adminDashboard.userManagement}
        </CardTitle>
        <CardDescription className="dark:text-gray-400">
          {t.adminDashboard.userManagementDesc}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Filtros */}
          <div className="flex justify-end gap-2">
            {(currentUser?.role === "admin" || currentUser?.role === "internal") && (
              <Button variant="outline" onClick={() => setImportUsersDialogOpen(true)}>
                {t.adminDashboard.importUsers}
              </Button>
            )}
            <Button onClick={() => setAddUserDialogOpen(true)}>{t.adminDashboard.addUser}</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger>
                <SelectValue placeholder={t.adminDashboard.filterByProfile} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.adminDashboard.allProfiles}</SelectItem>
                <SelectItem value="admin">{t.adminDashboard.roleAdminInternal}</SelectItem>
                <SelectItem value="internal">{t.adminDashboard.roleAdministrator}</SelectItem>
                <SelectItem value="analyst">{t.adminDashboard.roleAnalyst}</SelectItem>
                <SelectItem value="external">{t.adminDashboard.roleUser}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder={t.adminDashboard.filterByStatus} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.adminDashboard.allStatuses}</SelectItem>
                <SelectItem value="active">{t.adminDashboard.activeMale}</SelectItem>
                <SelectItem value="inactive">{t.adminDashboard.inactiveMale}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger>
                <SelectValue placeholder={t.adminDashboard.filterByCompany} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.adminDashboard.allCompanies}</SelectItem>
                <SelectItem value="no-company">{t.adminDashboard.noCompany}</SelectItem>
                {companiesForFilter.map((company: any) => (
                  <SelectItem key={company.id} value={company.id.toString()}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.total}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{users.length}</div>
            </div>
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.activeMale}</div>
              <div className="text-2xl font-bold text-green-600">
                {users.filter(u => u.is_active).length}
              </div>
            </div>
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.inactiveMale}</div>
              <div className="text-2xl font-bold text-red-600">
                {users.filter(u => !u.is_active).length}
              </div>
            </div>
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.results}</div>
              <div className="text-2xl font-bold text-blue-600">{filteredUsers.length}</div>
            </div>
          </div>

          {/* Lista de Usuários */}
          <div className="space-y-3">
            {filteredUsers.map((user) => (
              <div
                key={user.is_master ? String(user.id) : `${user.company?.id ?? "c"}-${user.id}`}
                className={`border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/70 ${
                  !user.is_active ? "opacity-60 bg-gray-50 dark:bg-gray-800/40" : ""
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                          user.is_active ? "bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-200" : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                        }`}>
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                        </div>
                      </div>
                      <Badge className={getRoleColor(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                      {isMasterUser(user) && (
                        <Badge className="bg-purple-100 text-purple-800">Master</Badge>
                      )}
                      {user.is_active ? (
                        <Badge className="bg-green-100 text-green-800">{t.adminDashboard.activeMale}</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">{t.adminDashboard.inactiveMale}</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                      {user.company ? (
                        <>
                          <span>{t.adminDashboard.company}: {user.company.name}</span>
                          <span>•</span>
                          <span>{t.adminDashboard.domain}: {user.company.domain}</span>
                        </>
                      ) : (
                        <span>{t.adminDashboard.internalUser}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="dark:border-gray-600 dark:hover:bg-gray-700"
                      onClick={() => handleOpenEditDialog(user)}
                      disabled={isMasterUser(user)}
                    >
                      <EditIcon className="h-4 w-4 mr-2" />
                      {t.adminDashboard.edit}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenResetPasswordDialog(user)}
                      disabled={isMasterUser(user)}
                    >
                      <KeyIcon className="h-4 w-4 mr-2" />
                      {t.adminDashboard.resetPassword}
                    </Button>
                    <Button
                      variant={user.is_active ? "destructive" : "default"}
                      size="sm"
                      onClick={() => {
                        const userId = typeof user.id === "string" ? parseInt(user.id, 10) : user.id;
                        if (!Number.isFinite(userId)) {
                          return;
                        }
                        handleToggleStatus(userId, user.is_active);
                      }}
                      disabled={isMasterUser(user)}
                    >
                      {user.is_active ? (
                        <>
                          <UserXIcon className="h-4 w-4 mr-2" />
                          {t.adminDashboard.deactivate}
                        </>
                      ) : (
                        <>
                          <UserCheckIcon className="h-4 w-4 mr-2" />
                          {t.adminDashboard.activate}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleOpenDeleteUserDialog(user)}
                      disabled={isMasterUser(user)}
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      {t.adminDashboard.delete}
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <UsersIcon className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                <p>{t.adminDashboard.noUsersFound}</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {/* Dialog de Edição */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.editUserDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.adminDashboard.editUserDialogDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">{t.adminDashboard.nameLabel}</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                onBlur={() => setEditFormData((prev) => ({ ...prev, name: formatPersonName(prev.name) }))}
                placeholder={t.adminDashboard.fullNamePlaceholder}
              />
            </div>
            <div>
              <Label htmlFor="edit-email">{t.adminDashboard.emailLabel}</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => {
                  const nextEmail = e.target.value;
                  setEditFormData((prev) => {
                    // Se já tem empresa definida, não sobrescrever automaticamente
                    if (prev.role === "admin") return { ...prev, email: nextEmail };
                    if (currentUser?.role !== "admin") return { ...prev, email: nextEmail };
                    if (prev.company_id && prev.company_id !== "none") return { ...prev, email: nextEmail };
                    const inferred = resolveCompanyIdFromEmail(nextEmail);
                    return { ...prev, email: nextEmail, company_id: inferred || prev.company_id };
                  });
                }}
                placeholder="email@exemplo.com"
              />
            </div>
            <div>
              <Label htmlFor="edit-role">Perfil</Label>
              <Select
                value={editFormData.role}
                onValueChange={(value) => {
                  setEditFormData({
                    ...editFormData,
                    role: value
                  });
                }}
                disabled={currentUser?.role !== "admin" && selectedUser?.role === "admin"}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
                <SelectContent>
                  {currentUser?.role === "admin" && (
                    <SelectItem value="admin">Admin Interno</SelectItem>
                  )}
                  <SelectItem value="internal">Administrador</SelectItem>
                  <SelectItem value="analyst">Analista</SelectItem>
                  <SelectItem value="external">Usuário</SelectItem>
                </SelectContent>
              </Select>
              {currentUser?.role !== "admin" && selectedUser?.role === "admin" && (
                <p className="text-xs text-gray-500 mt-1">
                  Apenas administradores podem alterar o perfil de outros administradores
                </p>
              )}
              {currentUser?.role === "internal" && selectedUser?.role !== "admin" && (
                <div className="text-xs text-blue-600 mt-1 flex flex-wrap items-center gap-1">
                  <span>Você pode alterar o cargo para</span>
                  <button
                    type="button"
                    className="underline hover:text-blue-700 font-medium"
                    onClick={() => setEditFormData((prev) => ({ ...prev, role: "external" }))}
                  >
                    Usuário
                  </button>
                  <span>,</span>
                  <button
                    type="button"
                    className="underline hover:text-blue-700 font-medium"
                    onClick={() => setEditFormData((prev) => ({ ...prev, role: "analyst" }))}
                  >
                    Analista
                  </button>
                  <span>ou</span>
                  <button
                    type="button"
                    className="underline hover:text-blue-700 font-medium"
                    onClick={() => setEditFormData((prev) => ({ ...prev, role: "internal" }))}
                  >
                    Administrador
                  </button>
                  <span>para usuários da sua empresa</span>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="edit-company">Empresa <span className="text-red-500">*</span></Label>
              {(currentUser?.role === "admin" || currentUser?.role === "internal") ? (
                <Select
                  value={editFormData.company_id?.toString() || "none"}
                  onValueChange={(value) => setEditFormData({ ...editFormData, company_id: value === "none" ? "none" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {isMasterAdminUser && <SelectItem value="none">Sem empresa</SelectItem>}
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id.toString()}>
                        {company.name} ({company.domain})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="edit-company"
                  value={selectedUser?.company?.name || "Empresa não definida"}
                  disabled
                  className="bg-gray-100"
                />
              )}
            </div>
            
            {/* Fila Preferencial - apenas para técnicos */}
            {(editFormData.role === "internal" || editFormData.role === "analyst" || editFormData.role === "admin") && (
              <div className="border-t pt-4">
                <Label htmlFor="edit-queue">Fila Preferencial de Atendimento</Label>
                <Select
                  value={editFormData.preferred_queue}
                  onValueChange={(value) => setEditFormData({ ...editFormData, preferred_queue: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a fila" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="N1">🟢 N1 - Primeiro Nível (Suporte Básico)</SelectItem>
                    <SelectItem value="N2">🟡 N2 - Segundo Nível (Suporte Avançado)</SelectItem>
                    <SelectItem value="N3">🔴 N3 - Terceiro Nível (Especialista)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Quando um chamado for transferido para este técnico, será movido automaticamente para esta fila
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleSaveUser} disabled={isSaving}>
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Importar Usuários */}
      <Dialog open={importUsersDialogOpen} onOpenChange={setImportUsersDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.importUsers}</DialogTitle>
            <DialogDescription>
              {t.adminDashboard.importUsersDialogDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Button variant="outline" onClick={handleDownloadUsersTemplate}>
                {t.adminDashboard.downloadTemplateReady}
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="import-users-file">{t.adminDashboard.spreadsheet}</Label>
              <div className="flex items-center gap-3 rounded-md border-2 border-gray-300 bg-gray-100 px-3 py-2">
                <label
                  htmlFor="import-users-file"
                  className="inline-flex h-9 cursor-pointer items-center rounded-md bg-gray-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-gray-700"
                >
                  {t.adminDashboard.chooseFile}
                </label>
                <span className="text-sm text-gray-700 truncate">
                  {importFile ? importFile.name : t.adminDashboard.noFileChosen}
                </span>
              </div>
              <Input
                id="import-users-file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="sr-only"
              />
              <p className="text-xs text-gray-500">
                Colunas extras: perfil (administrador/analista/usuario), fila_preferencial, ativo.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportUsersDialogOpen(false)}>
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleImportUsers} disabled={!importFile || isImporting}>
              {isImporting ? t.adminDashboard.importing : t.adminDashboard.import}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Adicionar Usuário */}
      <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.addUserDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.adminDashboard.addUserDialogDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-user-name">{t.adminDashboard.nameLabel}</Label>
              <Input
                id="new-user-name"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                onBlur={() => setNewUserName((prev) => formatPersonName(prev))}
                placeholder="Nome completo"
              />
            </div>
            <div>
              <Label htmlFor="new-user-email">E-mail</Label>
              <Input
                id="new-user-email"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="analista@empresa.com"
              />
            </div>
            <div>
              <Label htmlFor="new-user-role">Perfil</Label>
              <Select value={newUserRole} onValueChange={(value) => setNewUserRole(value as "admin" | "internal" | "analyst" | "external")}>
                <SelectTrigger id="new-user-role">
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
                <SelectContent>
                  {currentUser?.role === "admin" && (
                    <SelectItem value="admin">Admin Interno</SelectItem>
                  )}
                  <SelectItem value="internal">Administrador</SelectItem>
                  <SelectItem value="analyst">Analista</SelectItem>
                  <SelectItem value="external">Usuário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(currentUser?.role === "admin" || currentUser?.role === "internal") && (
              <div>
                <Label htmlFor="new-user-company">Empresa <span className="text-red-500">*</span></Label>
                <Select value={newUserCompanyId} onValueChange={setNewUserCompanyId}>
                  <SelectTrigger id="new-user-company">
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent>
                  {isMasterAdminUser && <SelectItem value="none">Sem Empresa</SelectItem>}
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id.toString()}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(newUserRole === "internal" || newUserRole === "analyst") && (
              <div>
                <Label htmlFor="new-user-queue">Fila preferencial</Label>
                <Select value={newUserQueue} onValueChange={setNewUserQueue}>
                  <SelectTrigger id="new-user-queue">
                    <SelectValue placeholder="Selecione a fila" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="N1">N1</SelectItem>
                    <SelectItem value="N2">N2</SelectItem>
                    <SelectItem value="N3">N3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAddUserDialogOpen(false);
              resetAddUserForm();
            }}>
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleCreateUser} disabled={!newUserName || !newUserEmail}>
              Criar Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Resetar Senha */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetar Senha</DialogTitle>
            <DialogDescription>
              Defina uma nova senha para {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-password">Nova Senha</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">
                A senha deve ter pelo menos 6 caracteres
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleResetPassword} disabled={isSaving || !newPassword || newPassword.length < 6}>
              {isSaving ? "Resetando..." : "Resetar Senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Excluir Usuário */}
      <Dialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir Usuário Permanentemente</DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. O usuário <strong>{selectedUser?.name}</strong> será removido permanentemente do sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangleIcon className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>Atenção:</strong> Esta ação é irreversível. Todos os dados do usuário serão perdidos.
              </AlertDescription>
            </Alert>
            <div>
              <Label htmlFor="confirmation-word">
                Para confirmar, digite a palavra: <strong className="text-lg text-blue-600">{confirmationWord}</strong>
              </Label>
              <Input
                id="confirmation-word"
                type="text"
                value={userInputConfirmation}
                onChange={(e) => setUserInputConfirmation(e.target.value.toUpperCase())}
                placeholder="Digite a palavra acima"
                className="mt-2 font-mono text-lg tracking-wider"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Digite exatamente a palavra mostrada acima para confirmar a exclusão.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteUserDialogOpen(false);
              setSelectedUser(null);
              setConfirmationWord("");
            }}>
              {t.adminDashboard.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={isSaving || !userInputConfirmation || userInputConfirmation.trim().toUpperCase() !== confirmationWord.toUpperCase()}
            >
              {isSaving ? "Excluindo..." : "Excluir Permanentemente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    {/* Importar Empresas: movido para CompaniesManagement */}
    </Card>
  );
};

interface CompanyFull {
  id: number;
  name: string;
  domain: string;
  phone: string | null;
  is_active: boolean;
  tickets_used?: number | null;
  tickets_unlimited?: boolean | null;
  users_count?: number | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
}

const CompaniesManagement = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [companies, setCompanies] = useState<CompanyFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyFull | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    domain: "",
    phone: "",
    is_active: true,
    tickets_unlimited: false,
    logo_url: "",
    primary_color: "#2563eb",
    secondary_color: "#4f46e5"
  });
  const [isSaving, setIsSaving] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const isMasterAdminUser = currentUser?.role === "admin" && !currentUser?.company_id;
  const canManageCompanies = currentUser?.role === "admin" || currentUser?.role === "internal";

  useEffect(() => {
    const user = getUser();
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setCurrentUser(parsedUser);
      } catch (error) {
        console.error("Erro ao parsear usuário:", error);
      }
    }
    fetchCompanies();
  }, []);

  const exportCompanyTemplate = () => {
    const headers = ["nome", "dominio", "telefone", "logo_url", "cor_primaria", "cor_secundaria", "ativo"];
    const exampleRows = [
      ["Empresa Exemplo LTDA", "empresa.com.br", "1133334444", "", "#2563eb", "#4f46e5", "true"],
      ["ACME Tecnologia", "acme.com.br", "11999998888", "https://site.com/logo.png", "#16a34a", "#22c55e", "true"],
      ["Beta Solucoes", "beta.com.br", "", "", "", "", "true"]
    ];

    const csvRows = [
      headers.join(";"),
      ...exampleRows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(";"))
    ];
    const csvContent = csvRows.join("\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-empresas.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportCompanies = async () => {
    if (!importFile) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Selecione um arquivo CSV.",
        variant: "destructive",
      });
      return;
    }

    try {
      setImporting(true);
      const token = getToken();
      const data = new FormData();
      data.append("file", importFile);
      const response = await fetch(getApiEndpoint("/api/companies/import"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Sucesso",
          description: `Empresas importadas: ${result.inserted.length}.`,
        });
        setImportDialogOpen(false);
        setImportFile(null);
        fetchCompanies();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Importação falhou");
      }
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível importar as empresas.",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/companies"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      } else {
        throw new Error("Erro ao buscar empresas");
      }
    } catch (error) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Não foi possível carregar as empresas.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!formData.name.trim() || !formData.domain.trim()) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Nome e domínio são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/companies"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          domain: formData.domain.trim(),
          phone: formData.phone.trim() || null,
          is_active: true,
          tickets_unlimited: formData.tickets_unlimited,
          logo_url: formData.logo_url?.trim() || null,
          primary_color: formData.primary_color || null,
          secondary_color: formData.secondary_color || null
        }),
      });

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: "Empresa criada com sucesso.",
        });
        setCreateDialogOpen(false);
        setFormData({ 
          name: "", 
          domain: "", 
          phone: "", 
          is_active: true,
          tickets_unlimited: false,
          logo_url: "",
          primary_color: "#2563eb",
          secondary_color: "#4f46e5"
        });
        fetchCompanies();
      } else {
        const data = await response.json();
        throw new Error(data.error || "Erro ao criar empresa");
      }
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível criar a empresa.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditDialog = (company: CompanyFull) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      domain: company.domain,
      phone: company.phone || "",
      is_active: company.is_active,
      tickets_unlimited: !!company.tickets_unlimited,
      logo_url: company.logo_url || "",
      primary_color: company.primary_color || "#2563eb",
      secondary_color: company.secondary_color || "#4f46e5"
    });
    setEditDialogOpen(true);
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany) return;

    if (!formData.name.trim() || !formData.domain.trim()) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: "Nome e domínio são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/companies/${selectedCompany.id}`), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          domain: formData.domain.trim(),
          phone: formData.phone.trim() || null,
          is_active: formData.is_active,
          tickets_unlimited: formData.tickets_unlimited,
          logo_url: formData.logo_url?.trim() || null,
          primary_color: formData.primary_color || null,
          secondary_color: formData.secondary_color || null
        }),
      });

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: "Empresa atualizada com sucesso.",
        });
        setEditDialogOpen(false);
        setSelectedCompany(null);
        fetchCompanies();
      } else {
        const data = await response.json();
        throw new Error(data.error || "Erro ao atualizar empresa");
      }
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível atualizar a empresa.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (company: CompanyFull) => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/companies/${company.id}/status`), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ is_active: !company.is_active }),
      });

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: `Empresa ${!company.is_active ? "ativada" : "desativada"} com sucesso.`,
        });
        fetchCompanies();
      } else {
        const data = await response.json();
        throw new Error(data.error || "Erro ao alterar status");
      }
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível alterar o status da empresa.",
        variant: "destructive",
      });
    }
  };

  const handleOpenDeleteDialog = (company: CompanyFull) => {
    setSelectedCompany(company);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;

    try {
      setIsSaving(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/companies/${selectedCompany.id}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: "Empresa deletada com sucesso.",
        });
        setDeleteDialogOpen(false);
        setSelectedCompany(null);
        fetchCompanies();
      } else {
        const data = await response.json();
        throw new Error(data.error || "Erro ao deletar empresa");
      }
    } catch (error: any) {
      toast({
        title: t.adminDashboard.errorTitle,
        description: error.message || "Não foi possível deletar a empresa.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = 
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.phone && company.phone.includes(searchTerm));
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && company.is_active) ||
      (statusFilter === "inactive" && !company.is_active);

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando empresas...</p>
        </div>
      </div>
    );
  }

  return (
    <Card className="dark:bg-gray-800 dark:border-gray-700">
      <CardHeader>
          <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center dark:text-white">
              <Building2Icon className="h-5 w-5 mr-2" />
              {t.adminDashboard.companyManagement}
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              {t.adminDashboard.companyManagementDesc}
            </CardDescription>
          </div>
          {canManageCompanies && (
            <div className="flex items-center gap-2">
              <Button onClick={() => setCreateDialogOpen(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                {t.adminDashboard.newCompany}
              </Button>
              {isMasterAdminUser && (
                <Button variant="outline" onClick={exportCompanyTemplate}>
                  {t.adminDashboard.downloadTemplate}
                </Button>
              )}
              <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
                {t.adminDashboard.importCsv}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t.adminDashboard.searchCompanyPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder={t.adminDashboard.filterByStatus} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.adminDashboard.all}</SelectItem>
                <SelectItem value="active">{t.adminDashboard.activePlural}</SelectItem>
                <SelectItem value="inactive">{t.adminDashboard.inactivePlural}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.total}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{companies.length}</div>
            </div>
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.activePlural}</div>
              <div className="text-2xl font-bold text-green-600">
                {companies.filter(c => c.is_active).length}
              </div>
            </div>
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.inactivePlural}</div>
              <div className="text-2xl font-bold text-red-600">
                {companies.filter(c => !c.is_active).length}
              </div>
            </div>
            <div className="border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t.adminDashboard.results}</div>
              <div className="text-2xl font-bold text-blue-600">{filteredCompanies.length}</div>
            </div>
          </div>

          {/* Lista de Empresas */}
          <div className="space-y-3">
            {filteredCompanies.map((company) => (
              <div
                key={company.id}
                className={`border rounded-lg p-4 dark:border-gray-700 dark:bg-gray-800/70 ${
                  !company.is_active ? "opacity-60 bg-gray-50 dark:bg-gray-800/40" : ""
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                        company.is_active ? "bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-200" : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                      }`}>
                        {company.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{company.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{company.domain}</div>
                        {company.phone && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">{company.phone}</div>
                        )}
                      </div>
                      {company.is_active ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">{t.adminDashboard.active}</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">{t.adminDashboard.inactive}</Badge>
                      )}
                      {isMasterAdminUser && (
                        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                          {company.tickets_unlimited ? `Tickets: ${t.adminDashboard.ticketsUnlimited}` : t.adminDashboard.ticketsCount(company.tickets_used ?? 0, 30)}
                        </Badge>
                      )}
                      {(company.users_count !== undefined && company.users_count !== null) && (
                        <Badge className="bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200">
                          {t.adminDashboard.peopleCount(company.users_count)}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {canManageCompanies && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="dark:border-gray-600 dark:hover:bg-gray-700"
                        onClick={() => handleOpenEditDialog(company)}
                      >
                        <EditIcon className="h-4 w-4 mr-2" />
                        {t.adminDashboard.edit}
                      </Button>
                      <Button
                        variant={company.is_active ? "destructive" : "default"}
                        size="sm"
                        onClick={() => handleToggleStatus(company)}
                      >
                        {company.is_active ? (
                          <>
                            <UserXIcon className="h-4 w-4 mr-2" />
                            {t.adminDashboard.deactivate}
                          </>
                        ) : (
                          <>
                            <UserCheckIcon className="h-4 w-4 mr-2" />
                            {t.adminDashboard.activate}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDeleteDialog(company)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 dark:border-red-800"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {filteredCompanies.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Building2Icon className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                <p>{t.adminDashboard.noCompaniesFound}</p>
                {canManageCompanies && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {t.adminDashboard.createFirstCompany}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {/* Dialog de Criar */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
<DialogTitle>{t.adminDashboard.newCompanyDialogTitle}</DialogTitle>
          <DialogDescription>
              {t.adminDashboard.newCompanyDialogDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="create-name">{t.adminDashboard.company} *</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Empresa XYZ Ltda"
              />
            </div>
            <div>
              <Label htmlFor="create-domain">Domínio *</Label>
              <Input
                id="create-domain"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value.toLowerCase() })}
                placeholder="exemplo.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Domínio sem www ou http://
              </p>
            </div>
            <div>
              <Label htmlFor="create-phone">Telefone (opcional)</Label>
              <Input
                id="create-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                placeholder="(11) 99999-9999"
              />
            </div>
            
            {/* Campos de Branding / White-Label */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                🎨 Personalização da Landing Page (opcional)
              </h4>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="create-logo">Logo da Empresa</Label>
                  
                  {/* Preview do logo */}
                  {formData.logo_url && (
                    <div className="mt-2 mb-3 p-4 border rounded-lg bg-gray-50 flex items-center gap-4">
                      <img 
                        src={formData.logo_url.startsWith('http') ? formData.logo_url : getApiEndpoint(formData.logo_url)} 
                        alt="Logo" 
                        className="w-16 h-16 object-contain"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">Logo selecionado</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, logo_url: '' })}
                      >
                        {t.adminDashboard.remove}
                      </Button>
                    </div>
                  )}
                  
                  {/* Input de arquivo */}
                  <Input
                    id="create-logo"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast({
                            title: t.adminDashboard.errorTitle,
                            description: "Imagem muito grande. Máximo 5MB.",
                            variant: "destructive",
                          });
                          return;
                        }
                        
                        const formDataUpload = new FormData();
                        formDataUpload.append('logo', file);
                        
                        try {
                          const token = getToken();
                          const response = await fetch(getApiEndpoint('/api/companies/upload-logo'), {
                            method: 'POST',
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                            body: formDataUpload,
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            setFormData({ ...formData, logo_url: data.logo_url });
                            toast({
                              title: "Sucesso",
                              description: "Logo enviado com sucesso!",
                            });
                          } else {
                            throw new Error('Erro ao fazer upload');
                          }
                        } catch (error) {
                          toast({
                            title: t.adminDashboard.errorTitle,
                            description: "Não foi possível enviar o logo.",
                            variant: "destructive",
                          });
                        }
                      }
                    }}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos aceitos: PNG, JPG, GIF, WEBP (máx. 5MB)
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="create-primary-color">Cor Primária</Label>
                    <div className="flex gap-2">
                      <Input
                        id="create-primary-color"
                        type="color"
                        value={formData.primary_color || '#2563eb'}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.primary_color || '#2563eb'}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        placeholder="#2563eb"
                        maxLength={7}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="create-secondary-color">Cor Secundária</Label>
                    <div className="flex gap-2">
                      <Input
                        id="create-secondary-color"
                        type="color"
                        value={formData.secondary_color || '#4f46e5'}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.secondary_color || '#4f46e5'}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        placeholder="#4f46e5"
                        maxLength={7}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Preview das cores */}
                {(formData.primary_color || formData.secondary_color) && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-600">Preview:</span>
                    <div 
                      className="w-12 h-12 rounded-lg shadow-sm"
                      style={{ 
                        background: `linear-gradient(135deg, ${formData.primary_color || '#2563eb'} 0%, ${formData.secondary_color || '#4f46e5'} 100%)` 
                      }}
                    ></div>
                    <span className="text-xs text-gray-500">
                      Gradiente para botões e elementos
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreateDialogOpen(false);
              setFormData({ 
                name: "", 
                domain: "", 
                phone: "", 
                is_active: true,
                logo_url: "",
                primary_color: "#2563eb",
                secondary_color: "#4f46e5"
              });
            }}>
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleCreateCompany} disabled={isSaving || !formData.name.trim() || !formData.domain.trim()}>
              {isSaving ? "Criando..." : "Criar Empresa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) {
          setImportFile(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.importCompanies}</DialogTitle>
            <DialogDescription>
              {t.adminDashboard.importCompaniesDialogDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex">
              <Button variant="outline" onClick={exportCompanyTemplate}>
                {t.adminDashboard.downloadTemplateReady}
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="import-file">CSV</Label>
              <div className="flex items-center gap-3 rounded-md border-2 border-gray-300 bg-gray-100 px-3 py-2">
                <label
                  htmlFor="import-file"
                  className="inline-flex h-9 cursor-pointer items-center rounded-md bg-gray-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-gray-700"
                >
                  {t.adminDashboard.chooseFile}
                </label>
                <span className="text-sm text-gray-700 truncate">
                  {importFile ? importFile.name : t.adminDashboard.noFileChosen}
                </span>
              </div>
              <Input
                id="import-file"
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files ? e.target.files[0] : null)}
                className="sr-only"
              />
            </div>
            <p className="text-xs text-gray-500">
              Mantenha o cabeçalho <strong>nome,dominio,telefone,logo_url,cor_primaria,cor_secundaria,ativo</strong>.
              Use ponto e vírgula como separador.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setImportDialogOpen(false);
              setImportFile(null);
            }}>
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleImportCompanies} disabled={importing || !importFile}>
              {importing ? t.adminDashboard.importing : t.adminDashboard.sendCsv}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Editar */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminDashboard.editCompanyDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.adminDashboard.editCompanyDialogDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Nome da Empresa *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Empresa XYZ Ltda"
              />
            </div>
            <div>
              <Label htmlFor="edit-domain">Domínio *</Label>
              <Input
                id="edit-domain"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value.toLowerCase() })}
                placeholder="exemplo.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Domínio sem www ou http://
              </p>
            </div>
            <div>
              <Label htmlFor="edit-phone">Telefone (opcional)</Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                placeholder="(11) 99999-9999"
              />
            </div>
            
            {/* Campos de Branding / White-Label */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                🎨 Personalização da Landing Page
              </h4>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-logo">Logo da Empresa</Label>
                  
                  {/* Preview do logo atual */}
                  {formData.logo_url && (
                    <div className="mt-2 mb-3 p-4 border rounded-lg bg-gray-50 flex items-center gap-4">
                      <img 
                        src={formData.logo_url.startsWith('http') ? formData.logo_url : getApiEndpoint(formData.logo_url)} 
                        alt="Logo atual" 
                        className="w-16 h-16 object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">Logo atual</p>
                        <p className="text-xs text-gray-500 truncate">{formData.logo_url}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, logo_url: '' })}
                      >
                        {t.adminDashboard.remove}
                      </Button>
                    </div>
                  )}
                  
                  {/* Input de arquivo */}
                  <Input
                    id="edit-logo"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      console.log('📤 Arquivo selecionado:', file?.name);
                      
                      if (file) {
                        console.log('📊 Tamanho:', (file.size / 1024).toFixed(2), 'KB');
                        console.log('📝 Tipo:', file.type);
                        
                        // Validar tamanho (5MB)
                        if (file.size > 5 * 1024 * 1024) {
                          console.log('❌ Arquivo muito grande');
                          toast({
                            title: t.adminDashboard.errorTitle,
                            description: "Imagem muito grande. Máximo 5MB.",
                            variant: "destructive",
                          });
                          return;
                        }
                        
                        // Upload
                        console.log('🚀 Iniciando upload...');
                        const formDataUpload = new FormData();
                        formDataUpload.append('logo', file);
                        
                        try {
                          const token = getToken();
                          const response = await fetch(getApiEndpoint('/api/companies/upload-logo'), {
                            method: 'POST',
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                            body: formDataUpload,
                          });
                          
                          console.log('📡 Resposta:', response.status);
                          
                          if (response.ok) {
                            const data = await response.json();
                            console.log('✅ Upload bem-sucedido:', data.logo_url);
                            setFormData({ ...formData, logo_url: data.logo_url });
                            toast({
                              title: "Sucesso",
                              description: "Logo enviado com sucesso!",
                            });
                          } else {
                            const errorData = await response.json();
                            console.log('❌ Erro na resposta:', errorData);
                            throw new Error(errorData.error || 'Erro ao fazer upload');
                          }
                        } catch (error) {
                          console.error('❌ Erro no upload:', error);
                          toast({
                            title: t.adminDashboard.errorTitle,
                            description: "Não foi possível enviar o logo.",
                            variant: "destructive",
                          });
                        }
                      }
                    }}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos aceitos: PNG, JPG, GIF, WEBP (máx. 5MB)
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-primary-color">Cor Primária</Label>
                    <div className="flex gap-2">
                      <Input
                        id="edit-primary-color"
                        type="color"
                        value={formData.primary_color || '#2563eb'}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.primary_color || '#2563eb'}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        placeholder="#2563eb"
                        maxLength={7}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-secondary-color">Cor Secundária</Label>
                    <div className="flex gap-2">
                      <Input
                        id="edit-secondary-color"
                        type="color"
                        value={formData.secondary_color || '#4f46e5'}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.secondary_color || '#4f46e5'}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        placeholder="#4f46e5"
                        maxLength={7}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Preview das cores */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-600">Preview:</span>
                  <div 
                    className="w-12 h-12 rounded-lg shadow-sm"
                    style={{ 
                      background: `linear-gradient(135deg, ${formData.primary_color || '#2563eb'} 0%, ${formData.secondary_color || '#4f46e5'} 100%)` 
                    }}
                  ></div>
                  <span className="text-xs text-gray-500">
                    Gradiente que aparecerá nos botões
                  </span>
                </div>
              </div>
            </div>
            
            {isMasterAdminUser && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-tickets-unlimited"
                  checked={!!formData.tickets_unlimited}
                  onChange={(e) => setFormData({ ...formData, tickets_unlimited: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="edit-tickets-unlimited" className="cursor-pointer">
                  Tickets ilimitados
                </Label>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="edit-active" className="cursor-pointer">
                Empresa ativa
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditDialogOpen(false);
              setSelectedCompany(null);
            }}>
              {t.adminDashboard.cancel}
            </Button>
            <Button onClick={handleUpdateCompany} disabled={isSaving || !formData.name.trim() || !formData.domain.trim()}>
              {isSaving ? t.adminDashboard.saving : t.adminDashboard.saveChanges}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Deletar */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja deletar a empresa "{selectedCompany?.name}"? Esta ação não pode ser desfeita.
              <br /><br />
              <strong className="text-red-600">
                Nota: Empresas com usuários vinculados não podem ser deletadas.
              </strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedCompany(null);
            }}>
              {t.adminDashboard.cancel}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteCompany} 
              disabled={isSaving}
            >
              {isSaving ? "Deletando..." : "Sim, Deletar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminDashboard;
